import React, { Suspense, useCallback, useMemo } from "react";
import {
  Box,
  Alert,
  CircularProgress,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Paper,
} from "@mui/material";

// Import question type components
import MultipleChoiceComponent from "./questionTypes/MultipleChoiceQuestion";
import ReadingComprehensionComponent from "./questionTypes/ReadingComprehensionQuestion";
import AudioListeningComponent from "./questionTypes/AudioListeningQuestion";
import SentenceBuildingComponent from "./questionTypes/SentenceBuilderQuestion";
import ReferenceReadingComponent from "./questionTypes/ReferenceReadingQuestion";

// Lazy load additional components
const SwitchCardComponent = React.lazy(
  () => import("./questionTypes/SwitchCardQuestion")
);
const PartsOfSpeechComponent = React.lazy(
  () => import("./questionTypes/PartsOfSpeechQuestion")
);
const FillInBlankMCComponent = React.lazy(
  () => import("./questionTypes/FillInBlankMCQuestion")
);
const SpeakingPracticeComponent = React.lazy(
  () => import("./questionTypes/SpeakingPracticeQuestion")
);
const InstructionalContentComponent = React.lazy(
  () => import("./questionTypes/InstructionalContentQuestion")
);
const TrueFalseSelectionComponent = React.lazy(
  () => import("./questionTypes/TrueFalseSelectionQuestion")
);

// Question type names for display
const QUESTION_TYPE_NAMES: Record<string, string> = {
  "1": "Multiple Choice",
  "2": "Reading Comprehension",
  "3": "Audio Listening",
  "4": "Sentence Building",
  "6": "Reference Reading",
  "8": "Switch Card",
  "9": "Parts of Speech",
  "10": "Fill in Blank",
  "11": "Speaking Practice",
  "12": "Instructional Content",
  "13": "True/False Selection",
};

interface QuestionRendererProps {
  question: any;
  userAnswer?: any;
  reviewMode?: boolean;
  onAnswerChange: (answer: any) => void;
  onComplete?: (isCorrect: boolean, timeSpent: number) => void;
  validateAnswer?: (question: any, answer: any) => boolean;
  disabled?: boolean;
  showResults?: boolean;
}

const LoadingFallback: React.FC = () => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      minHeight: 200,
      gap: 2,
    }}
  >
    <CircularProgress size={32} />
    <Typography variant="body2" color="text.secondary">
      Loading question...
    </Typography>
  </Box>
);

const QuestionRenderer: React.FC<QuestionRendererProps> = ({
  question,
  userAnswer,
  reviewMode = false,
  onAnswerChange,
  onComplete,
  validateAnswer,
  disabled = false,
  showResults = false,
}) => {
  // Basic validation
  if (!question) {
    return (
      <Alert severity="error" sx={{ my: 2 }}>
        <Typography variant="body2">No question data provided</Typography>
      </Alert>
    );
  }

  if (!question.type || !question.id) {
    return (
      <Alert severity="error" sx={{ my: 2 }}>
        <Typography variant="body2">
          Invalid question: Missing type or ID
        </Typography>
      </Alert>
    );
  }

  // Common props for all question types
  const commonProps = {
    question,
    userAnswer,
    onAnswerChange,
    onComplete,
    disabled: disabled || reviewMode,
    reviewMode,
    showResults: showResults || reviewMode,
  };

  // Render based on question type
  switch (question.type) {
    case "1": // Multiple Choice
      return <MultipleChoiceComponent {...commonProps} />;

    case "2": // Reading Comprehension
      return <ReadingComprehensionComponent {...commonProps} />;

    case "3": // Audio Listening
      return <AudioListeningComponent {...commonProps} />;

    case "4": // Sentence Building
      return <SentenceBuildingComponent {...commonProps} />;

    case "6": // Reference Reading
      return <ReferenceReadingComponent {...commonProps} />;

    case "8": // Switch Card
      return (
        <Suspense fallback={<LoadingFallback />}>
          <SwitchCardComponent {...commonProps} />
        </Suspense>
      );

    case "9": // Parts of Speech
      return (
        <Suspense fallback={<LoadingFallback />}>
          <PartsOfSpeechComponent {...commonProps} />
        </Suspense>
      );

    case "10": // Fill in Blank MC
      return (
        <Suspense fallback={<LoadingFallback />}>
          <FillInBlankMCComponent {...commonProps} />
        </Suspense>
      );

    case "11": // Speaking Practice
      return (
        <Suspense fallback={<LoadingFallback />}>
          <SpeakingPracticeComponent {...commonProps} />
        </Suspense>
      );

    case "12": // Instructional Content
      return (
        <Suspense fallback={<LoadingFallback />}>
          <InstructionalContentComponent {...commonProps} />
        </Suspense>
      );

    case "13": // True/False Selection
      return (
        <Suspense fallback={<LoadingFallback />}>
          <TrueFalseSelectionComponent {...commonProps} />
        </Suspense>
      );

    default:
      // Unsupported question type
      return (
        <Alert severity="warning" sx={{ my: 2 }}>
          <Typography variant="body2">
            Question type "{question.type}" (
            {QUESTION_TYPE_NAMES[question.type] || "Unknown"}) is not currently
            supported
          </Typography>
        </Alert>
      );
  }
};

export default QuestionRenderer;
